import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/forgot-password?email=${encodeURIComponent(email)}`,
        {},
        { withCredentials: true }
      );

      setSubmitted(true);
      
      toast({
        title: "Check Your Email",
        description: "If your email is registered, you will receive a password reset link shortly.",
      });
      // Clear the email field
      setEmail('');
    } catch (error) {
      console.error('Forgot password error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to process request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Back to Login Link */}
        <Link 
          to="/login" 
          className="inline-flex items-center text-gray-600 hover:text-navy-500 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl">Forgot Password?</CardTitle>
            <p className="text-center text-gray-600 mt-2">
              Enter your email address and we'll send you a link to reset your password
            </p>
          </CardHeader>
          <CardContent>
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-mint-500 hover:bg-mint-600 text-white py-6"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-center mb-3">
                    <Mail className="w-12 h-12 text-green-600" />
                  </div>
                  <p className="text-green-800 text-center font-medium mb-2">
                    ✓ Check Your Email
                  </p>
                  <p className="text-sm text-green-700 text-center">
                    If your email is registered with us, you will receive a password reset link shortly.
                  </p>
                  <p className="text-xs text-green-600 text-center mt-3">
                    Please check your inbox and spam folder.
                  </p>
                </div>

                <div className="text-center space-y-2">
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setEmail('');
                      setResetLink('');
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    Try Another Email
                  </Button>
                  
                  <Link to="/login">
                    <Button variant="ghost" className="w-full text-navy-500">
                      Return to Login
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Remember your password?{' '}
                <Link to="/login" className="text-navy-500 hover:text-navy-600 font-semibold">
                  Login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
